'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import ChatAttachment, { ChatMediaPreview, type ChatMedia } from '@/components/ChatAttachment';
import { createClient } from '@/lib/local-api/client';
import { XMarkIcon, PaperAirplaneIcon, ChatBubbleLeftRightIcon, ChevronDownIcon, NoSymbolIcon, BellSlashIcon } from '@heroicons/react/24/outline';

interface Friend {
  id: string;
  full_name: string;
  role?: string;
}

interface Message extends ChatMedia {
  id: string;
  sender_id: string;
  receiver_id: string;
  content: string;
  read_at: string | null;
  created_at: string;
}

interface ChatPanelProps {
  currentUserId: string;
  friends: Friend[];
}

export default function ChatPanel({ currentUserId, friends }: ChatPanelProps) {
  return <AccountChatPanel key={currentUserId} currentUserId={currentUserId} friends={friends} />;
}

function AccountChatPanel({ currentUserId, friends }: ChatPanelProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeFriend, setActiveFriend] = useState<Friend | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [unreadCounts, setUnreadCounts] = useState<Record<string, number>>({});
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [showFriendMenu, setShowFriendMenu] = useState<string | null>(null);
  const messageListRef = useRef<HTMLDivElement>(null);
  const followLatest = useRef(true);
  const inputRef = useRef<HTMLInputElement>(null);
  const messageRequest = useRef(0);

  const totalUnread = Object.values(unreadCounts).reduce((a, b) => a + b, 0);

  const fetchUnreadCounts = useCallback(async () => {
    if (!currentUserId) return;
    const supabase = createClient();
    const { data } = await supabase
      .from('direct_messages')
      .select('sender_id')
      .eq('receiver_id', currentUserId)
      .is('read_at', null);
    if (data) {
      const counts: Record<string, number> = {};
      data.forEach((m: any) => {
        counts[m.sender_id] = (counts[m.sender_id] || 0) + 1;
      });
      setUnreadCounts(counts);
    }
  }, [currentUserId]);

  const fetchMessages = useCallback(async (friendId: string) => {
    const requestId = ++messageRequest.current;
    followLatest.current = true;
    setMessages([]);
    setLoadingMessages(true);
    const supabase = createClient();
    const { data } = await supabase
      .from('direct_messages')
      .select('*')
      .or(
        `and(sender_id.eq.${currentUserId},receiver_id.eq.${friendId}),and(sender_id.eq.${friendId},receiver_id.eq.${currentUserId})`
      )
      .order('created_at', { ascending: true });
    if (requestId !== messageRequest.current) return;
    if (data) setMessages(data as Message[]);

    // Mark messages from this friend as read
    await supabase
      .from('direct_messages')
      .update({ read_at: new Date().toISOString() })
      .eq('sender_id', friendId)
      .eq('receiver_id', currentUserId)
      .is('read_at', null);

    setUnreadCounts((prev) => { const next = { ...prev }; delete next[friendId]; return next; });
    if (requestId === messageRequest.current) setLoadingMessages(false);
  }, [currentUserId]);

  useEffect(() => {
    fetchUnreadCounts();
  }, [fetchUnreadCounts]);

  useEffect(() => {
    if (!activeFriend) return;
    fetchMessages(activeFriend.id);

    const supabase = createClient();
    const channel = supabase
      .channel(`dm_${currentUserId}_${activeFriend.id}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'direct_messages',
        filter: `receiver_id=eq.${currentUserId}`,
      }, (payload) => {
        const msg = payload.new as Message;
        if (msg.sender_id === activeFriend.id) {
          setMessages((prev) => prev.some((existing) => existing.id === msg.id) ? prev : [...prev, msg]);
          // Mark as read immediately
          void supabase.from('direct_messages').update({ read_at: new Date().toISOString() }).eq('id', msg.id).then(() => fetchUnreadCounts());
        }
      })
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'direct_messages',
        filter: `sender_id=eq.${currentUserId}`,
      }, (payload) => {
        const msg = payload.new as Message;
        if (msg.receiver_id === activeFriend.id) {
          setMessages((prev) => {
            if (prev.find((m) => m.id === msg.id)) return prev;
            return [...prev, msg];
          });
        }
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [activeFriend, currentUserId, fetchMessages]);

  useEffect(() => {
    if (!currentUserId) return;
    const supabase = createClient();
    const channel = supabase
      .channel(`unread_${currentUserId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'direct_messages',
        filter: `receiver_id=eq.${currentUserId}`,
      }, (payload) => {
        const msg = payload.new as Message;
        if (!activeFriend || msg.sender_id !== activeFriend.id) {
          setUnreadCounts((prev) => ({ ...prev, [msg.sender_id]: (prev[msg.sender_id] || 0) + 1 }));
        }
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [currentUserId, activeFriend]);

  useEffect(() => {
    const list = messageListRef.current;
    if (list && followLatest.current) list.scrollTop = list.scrollHeight;
  }, [messages, loadingMessages]);

  const handleSend = async () => {
    if (!activeFriend || !messageText.trim() || sending) return;
    setSending(true);
    const supabase = createClient();
    const content = messageText.trim();
    setMessageText('');
    const { data, error } = await supabase.from('direct_messages').insert({
      sender_id: currentUserId,
      receiver_id: activeFriend.id,
      content,
    }).select().single();
    if (!error && data) {
      followLatest.current = true;
      setMessages((prev) => {
        if (prev.find((m) => m.id === data.id)) return prev;
        return [...prev, data as Message];
      });
    }
    setSending(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleBlockFromChat = async (friendId: string, friendName: string) => {
    setShowFriendMenu(null);
    const supabase = createClient();
    const { error } = await supabase.from('user_blocks').upsert({ blocker_id: currentUserId, blocked_id: friendId });
    if (!error) {
      setActiveFriend(null);
      setIsOpen(false);
    }
  };

  const handleMuteFromChat = async (friendId: string) => {
    setShowFriendMenu(null);
    const supabase = createClient();
    await supabase.from('user_mutes').upsert({ muter_id: currentUserId, muted_id: friendId });
  };

  const openChat = (friend: Friend) => {
    setActiveFriend(friend);
    setIsOpen(true);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const formatTime = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const getInitials = (name: string) =>
    name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();

  if (friends.length === 0) return null;

  return (
    <>
      {/* Floating Chat Button */}
      <div className="fixed bottom-32 right-6 z-40 lg:bottom-16 lg:right-6 flex flex-col items-end gap-2">
        {/* Friends list popup */}
        {isOpen && !activeFriend && (
          <div className="bg-card border border-border rounded-2xl shadow-2xl w-72 overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-[#1877f2]">
              <ChatBubbleLeftRightIcon className="text-white" style={{ width: 16, height: 16 }} />
              <h3 className="text-sm font-bold text-white flex-1">Messages</h3>
              <button onClick={() => setIsOpen(false)} className="text-white/80 hover:text-white transition-colors">
                <XMarkIcon style={{ width: 16, height: 16 }} />
              </button>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {friends.length === 0 ? (
                <div className="p-6 text-center">
                  <p className="text-xs text-muted-foreground">No friends yet. Accept friend requests to start chatting!</p>
                </div>
              ) : (
                friends.map((friend) => {
                  const unread = unreadCounts[friend.id] || 0;
                  return (
                    <button
                      key={friend.id}
                      onClick={() => openChat(friend)}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/50 transition-colors text-left"
                    >
                      <div className="relative">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                          {getInitials(friend.full_name)}
                        </div>
                        <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-green-500 border-2 border-card" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-foreground truncate">{friend.full_name}</p>
                        <p className="text-[10px] text-muted-foreground">Click to chat</p>
                      </div>
                      {unread > 0 && (
                        <span className="w-5 h-5 rounded-full bg-[#1877f2] text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                          {unread > 9 ? '9+' : unread}
                        </span>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* Active chat window */}
        {isOpen && activeFriend && (
          <div className="bg-card border border-border rounded-2xl shadow-2xl w-80 overflow-hidden flex flex-col" style={{ height: 420, maxHeight: 'calc(100dvh - 12rem)' }}>
            {/* Chat header */}
            <div className="flex items-center gap-2.5 px-3 py-2.5 border-b border-border bg-[#1877f2] flex-shrink-0">
              <div className="relative">
                <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white font-bold text-xs">
                  {getInitials(activeFriend.full_name)}
                </div>
                <span className="absolute bottom-0 right-0 w-2 h-2 rounded-full bg-green-400 border border-[#1877f2]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-white truncate">{activeFriend.full_name}</p>
                <p className="text-[10px] text-white/70">Active now</p>
              </div>
              {/* Block/Mute menu in chat header */}
              <div className="relative">
                <button
                  onClick={() => setShowFriendMenu(showFriendMenu === activeFriend.id ? null : activeFriend.id)}
                  className="text-white/80 hover:text-white transition-colors p-1"
                  title="More options"
                >
                  <span className="text-sm font-bold leading-none">···</span>
                </button>
                {showFriendMenu === activeFriend.id && (
                  <div className="absolute right-0 top-full mt-1 z-50 bg-card border border-border rounded-xl shadow-xl overflow-hidden min-w-[150px]">
                    <button
                      onClick={() => handleMuteFromChat(activeFriend.id)}
                      className="w-full flex items-center gap-2 px-3 py-2.5 text-xs text-foreground hover:bg-muted transition-colors text-left"
                    >
                      <BellSlashIcon style={{ width: 14, height: 14 }} className="text-amber-500" />
                      Mute User
                    </button>
                    <button
                      onClick={() => handleBlockFromChat(activeFriend.id, activeFriend.full_name)}
                      className="w-full flex items-center gap-2 px-3 py-2.5 text-xs text-red-500 hover:bg-red-500/10 transition-colors text-left"
                    >
                      <NoSymbolIcon style={{ width: 14, height: 14 }} />
                      Block User
                    </button>
                  </div>
                )}
              </div>
              <button onClick={() => setActiveFriend(null)} className="text-white/80 hover:text-white transition-colors p-1">
                <ChevronDownIcon style={{ width: 16, height: 16 }} />
              </button>
              <button onClick={() => { setActiveFriend(null); setIsOpen(false); }} className="text-white/80 hover:text-white transition-colors p-1">
                <XMarkIcon style={{ width: 16, height: 16 }} />
              </button>
            </div>

            {/* Messages */}
            <div ref={messageListRef}
              onScroll={(event) => {
                const list = event.currentTarget;
                followLatest.current = list.scrollHeight - list.scrollTop - list.clientHeight < 64;
              }}
              className="min-h-0 overscroll-contain flex-1 overflow-y-auto p-3 space-y-2">
              {loadingMessages ? (
                <div className="flex items-center justify-center h-full">
                  <span className="w-5 h-5 border-2 border-[#1877f2]/30 border-t-[#1877f2] rounded-full animate-spin" />
                </div>
              ) : messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full gap-2 text-center">
                  <div className="w-12 h-12 rounded-full bg-[#1877f2]/10 flex items-center justify-center">
                    <ChatBubbleLeftRightIcon className="text-[#1877f2]" style={{ width: 20, height: 20 }} />
                  </div>
                  <p className="text-xs text-muted-foreground">Say hello to {activeFriend.full_name}!</p>
                </div>
              ) : (
                messages.map((msg) => {
                  const isMine = msg.sender_id === currentUserId;
                  return (
                    <div key={msg.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[75%] rounded-2xl px-3 py-2 ${isMine ? 'bg-[#1877f2] text-white rounded-br-sm' : 'bg-muted text-foreground rounded-bl-sm'}`}>
                        <ChatMediaPreview message={msg} /><p className="text-sm leading-relaxed break-words">{msg.content}</p>
                        <p className={`text-[9px] mt-0.5 ${isMine ? 'text-white/60' : 'text-muted-foreground'}`}>{formatTime(msg.created_at)}</p>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Input */}
            <div className="flex items-center gap-2 px-3 py-2.5 border-t border-border flex-shrink-0">
              <ChatAttachment key={activeFriend.id} senderId={currentUserId} receiverId={activeFriend.id} onSent={(message) => {
                  followLatest.current = true;
                  setMessages((previous) => previous.some((item) => item.id === message.id) ? previous : [...previous, message]);

                }} />
                <input
                ref={inputRef}
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Aa"
                className="min-w-0 flex-1 bg-muted rounded-full px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-[#1877f2]/40"
              />
              <button
                onClick={handleSend}
                disabled={!messageText.trim() || sending}
                className="w-8 h-8 rounded-full bg-[#1877f2] text-white flex items-center justify-center hover:bg-[#166fe5] disabled:opacity-40 disabled:cursor-not-allowed transition-all active:scale-95 flex-shrink-0"
              >
                {sending ? (
                  <span className="w-3 h-3 border border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <PaperAirplaneIcon style={{ width: 14, height: 14 }} />
                )}
              </button>
            </div>
          </div>
        )}

        {/* Chat toggle button */}
        <button
          onClick={() => { setIsOpen((v) => !v); if (activeFriend) setActiveFriend(null); }}
          className="w-11 h-11 rounded-full bg-[#1877f2] text-white shadow-lg hover:bg-[#166fe5] transition-all active:scale-95 flex items-center justify-center relative"
        >
          <ChatBubbleLeftRightIcon style={{ width: 20, height: 20 }} />
          {totalUnread > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center border-2 border-background">
              {totalUnread > 9 ? '9+' : totalUnread}
            </span>
          )}
        </button>
      </div>
    </>
  );
}
